import styled from "@emotion/styled";
import {
  AppError,
  Button,
  FieldError,
  FlexRow,
  Input,
  Label,
  Link,
  RequiredText,
  Text,
} from "atoms";
import { AppSuccess } from "atoms/AppError";
import { fieldOptions, validators } from "utils/form";
import { useToggle } from "utils/hooks";
import { SForm, PwdToggleButton, PwdWrapper } from "./EmailSignIn";
import { useUpdatePasswordForm } from "../api/useUpdatePasswordForm";
import { User } from "../types";

type UpdatePasswordProps = {
  showCancel?: boolean;
  user: User;
};

function UpdatePassword({ user, showCancel = true }: UpdatePasswordProps) {
  // Toggle state to show/hide password inputs
  const [isShowingPwd, togglePwd] = useToggle(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    mutation: { mutate, isSuccess, isLoading, error },
  } = useUpdatePasswordForm({ defaultValues: { email: user?.email } });

  // Watch entered password to validate with confirmation
  const enteredPwd = watch("password");

  const onSubmit = handleSubmit((fields) => mutate(fields));

  return !isSuccess ? (
    <SForm onSubmit={onSubmit}>
      <HiddenInput type="text" {...register("email")} autoComplete="username" />

      <PwdWrapper>
        <Label>
          Create New Password <RequiredText />
          <Input
            {...register("password", {
              ...fieldOptions.required,
              validate: validators.validPassword,
            })}
            type={isShowingPwd ? "text" : "password"}
            autoComplete="new-password"
            autoCapitalize="none"
          />
          <Text size="xxs" as="span">
            Passwords must be 10 or more characters.
          </Text>
          {errors.password && (
            <FieldError>{errors.password.message}</FieldError>
          )}
        </Label>

        <PwdToggleButton variant="blank" size="xxs" onClick={togglePwd}>
          {isShowingPwd ? "Hide" : "Show"}
        </PwdToggleButton>
      </PwdWrapper>

      <Label>
        Confirm New Password <RequiredText />
        <Input
          {...register("confirm", {
            ...fieldOptions.required,
            validate: (confirmation) =>
              confirmation === enteredPwd || "Your passwords do not match",
          })}
          type={isShowingPwd ? "text" : "password"}
          autoComplete="new-password"
          autoCapitalize="none"
        />
        {errors.confirm && <FieldError>{errors.confirm.message}</FieldError>}
      </Label>

      {error instanceof Error && (
        <AppError className="AppError">{error.message}</AppError>
      )}

      <FlexRow justifyContent="center">
        <Button type="submit" disabled={isLoading} size="sm">
          Save Password
        </Button>

        {showCancel && (
          <Link to="/profile" variant="secondary" size="sm">
            Cancel
          </Link>
        )}
      </FlexRow>
    </SForm>
  ) : (
    <AppSuccess>
      Success!
      <br />
      Your password has been updated.
    </AppSuccess>
  );
}

const HiddenInput = styled.input({
  border: "none",
  height: 1,
  margin: 0,
  padding: 0,
  position: "absolute",
  left: -9999,
  width: 1,
});

export { UpdatePassword };
